import React, { useState } from "react";
import { react2Angular } from "~/Core/React2Angular";
import { Website } from "~/Generated/Entities";
import { TS } from "~/Services/TS";
import { navigate } from "~/Helpers/Navigation";
import { Dictionary } from "~/Types/Common";
import { useGetWebsites } from "~/Generated/OData";
import { useGetUserData } from "~/Hooks/Api/Account";

const Dashboard = ({
    adminSessionService,
    websiteService,
    permissions,
}: {
    adminSessionService: any;
    websiteService: any;
    permissions: Dictionary<{ canView: boolean; canCreate: boolean; canEdit: boolean }>;
}) => {
    const [contentEditorModalIsOpen, setContentEditorModalIsOpen] = useState<boolean>(false);
    const [importExportModalIsOpen, setImportExportModalIsOpen] = useState<boolean>(false);
    const [actionName, setActionName] = useState<string>("import");
    const [definitionName, setDefinitionName] = useState<string>("products");
    const { data: websitesData } = useGetWebsites();
    const { data: userProfile } = useGetUserData();

    if (!userProfile) {
        return null;
    }

    const openExternalLink = (location: string, name: string) => {
        telemetry.track("Clicked link for " + name);
        const win = window.open(location, "_blank");
        win?.focus();
    };

    const openImportExportModal = () => {
        setActionName("import");
        setDefinitionName("products");
        setImportExportModalIsOpen(true);
    };

    const openWebsiteContentAdmin = (website: Website) => {
        const form = document.createElement("form");
        form.method = "post";
        form.target = "_blank";
        form.action = websiteService.getWebsiteContentAdminUrl(website, true, false);
        form.innerHTML =
            `<input type="hidden" name="jwtToken" value="${adminSessionService.getAccessToken()}" />` +
            `<input type="hidden" name="refreshToken" value="${adminSessionService.getRefreshToken()}" />`;

        document.body.append(form);
        form.submit();
        form.remove();
    };

    const handleActionNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setActionName(e.target.value);
    };

    const handleDefinitionNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setDefinitionName(e.target.value);
    };

    const actionNameOptionFor = (value: string, label: string) => {
        return (
            <span className="formitem">
                <label className="better-label">
                    <input
                        type="radio"
                        name="actionName"
                        value={value}
                        checked={actionName === value}
                        onChange={handleActionNameChange}
                        className="inline"
                    />
                    {TS(label)}
                </label>
            </span>
        );
    };

    const definitionNameOptionFor = (key: string, value: string, label: string) => {
        const permission = permissions[key];
        if (!permission || (!permission.canCreate && !permission.canEdit)) {
            return null;
        }

        return (
            <li>
                <label className="better-label">
                    <input
                        type="radio"
                        name="definitionName"
                        value={value}
                        checked={definitionName === value}
                        onChange={handleDefinitionNameChange}
                        className="inline"
                    />
                    {TS(label)}
                </label>
            </li>
        );
    };

    const userHasRole = (roleName: string) => {
        return userProfile.roles.some(o => o.toLowerCase() === roleName.toLowerCase());
    };

    const contentEditorIsEnabled =
        userHasRole("ISC_Implementer") ||
        userHasRole("ISC_FrontEndDev") ||
        userHasRole("ISC_ContentAdmin") ||
        userHasRole("ISC_ContentApprover") ||
        userHasRole("ISC_ContentEditor") ||
        userHasRole("CMS_ContentEditor") ||
        userHasRole("CMS_ContentDeveloper") ||
        userHasRole("CMS_SiteEditor") ||
        userHasRole("CMS_Publisher") ||
        userHasRole("CMS_SystemAdmin");

    return (
        <>
            <div className="grid-block vertical dashboard" id="tst_homePage">
                <div className="grid-content">
                    <h1>{TS("Dashboard")}</h1>
                    <h2>{TS("Quick Links")}</h2>
                </div>
                <div className="grid-block wrap">
                    {(permissions.customerOrder?.canView || permissions.orderHistory?.canView) && (
                        <div className="small-12 medium-6 large-4 grid-block">
                            <div className="grid-block dashboard-quicklink">
                                <i className="fa-light fa-cart-shopping dashboard-quicklink__image"></i>
                                <div className="vertical grid-block">
                                    <h3 className="dashboard-quicklink__title">{TS("Orders")}</h3>
                                    <p className="no-margin">{TS("View recent order activity and order details.")}</p>
                                    <div className="dashboard-quicklink__links">
                                        {permissions.orderHistory?.canView && (
                                            <>
                                                <a href="data/orderHistories">{TS("Order History")}</a>
                                                <span>&#8226;</span>
                                            </>
                                        )}
                                        {permissions.customerOrder?.canView && (
                                            <a href="data/customerOrders">{TS("Cart History")}</a>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    {contentEditorIsEnabled && (
                        <div className="small-12 medium-6 large-4 grid-block">
                            <div
                                className="grid-block dashboard-quicklink dashboard-quicklink__link"
                                onClick={() => setContentEditorModalIsOpen(true)}
                            >
                                <i className="fa-light fa-file-pen dashboard-quicklink__image dashboard-quicklink__image--cms"></i>
                                <div className="vertical grid-block">
                                    <h3 className="dashboard-quicklink__title">{TS("Content Editor (CMS)")}</h3>
                                    <p className="no-margin">{TS("Create, manage and approve website content")}.</p>
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="small-12 medium-6 large-4 grid-block">
                        <div
                            className="grid-block dashboard-quicklink dashboard-quicklink__link"
                            onClick={() => openImportExportModal()}
                        >
                            <i className="fa-light fa-arrow-up-arrow-down dashboard-quicklink__image dashboard-quicklink__image--import-export"></i>
                            <div className="vertical grid-block">
                                <h3 className="dashboard-quicklink__title">{TS("Import / Export")}</h3>
                                <p className="no-margin">{TS("Import or export data")}.</p>
                            </div>
                        </div>
                    </div>
                    {permissions.website?.canView && (
                        <div className="small-12 medium-6 large-4 grid-block">
                            <div
                                className="grid-block dashboard-quicklink dashboard-quicklink__link"
                                onClick={() => navigate("data/websites")}
                            >
                                <i className="fa-light fa-browsers dashboard-quicklink__image dashboard-quicklink__image--websites"></i>
                                <div className="vertical grid-block">
                                    <h3 className="dashboard-quicklink__title">{TS("Websites")}</h3>
                                    <p className="no-margin">
                                        {TS("Create and manage websites, microsites and more.")}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                    {permissions.product?.canView && (
                        <div className="small-12 medium-6 large-4 grid-block">
                            <div className="grid-block dashboard-quicklink">
                                <i className="fa-light fa-cubes dashboard-quicklink__image dashboard-quicklink__image--products"></i>
                                <div className="vertical grid-block">
                                    <h3 className="dashboard-quicklink__title">{TS("Products")}</h3>
                                    <p className="no-margin">
                                        {TS("Create and manage products sold on your websites")}.
                                    </p>
                                    <div className="dashboard-quicklink__links">
                                        <a href="data/products">{TS("View")}</a>&#8226;
                                        {permissions.product?.canCreate && (
                                            <>
                                                <a href="data/products/new">{TS("Create")}</a>
                                                <span>&#8226;</span>
                                            </>
                                        )}
                                        {(permissions.product?.canCreate || permissions.product?.canEdit) && (
                                            <>
                                                <a href="import/products">{TS("Import")}</a>
                                                <span>&#8226;</span>
                                            </>
                                        )}
                                        <a href="export/products">{TS("Export")}</a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    {permissions.promotion?.canView && (
                        <div className="small-12 medium-6 large-4 grid-block">
                            <div className="grid-block dashboard-quicklink">
                                <i className="fa-light fa-badge-percent dashboard-quicklink__image dashboard-quicklink__image--promotions"></i>
                                <div className="vertical grid-block">
                                    <h3 className="dashboard-quicklink__title">{TS("Promotions")}</h3>
                                    <p className="no-margin">{TS("Create and manage promotions for your websites.")}</p>
                                    <div className="dashboard-quicklink__links">
                                        <a href="data/promotions">{TS("View")}</a>
                                        {permissions.promotion?.canCreate && (
                                            <>
                                                <span>&#8226;</span>
                                                <a href="data/promotions/new">{TS("Create")}</a>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {permissions.customer?.canView && (
                        <div className="small-12 medium-6 large-4 grid-block">
                            <div className="grid-block dashboard-quicklink">
                                <i className="fa-light fa-users dashboard-quicklink__image dashboard-quicklink__image--customers"></i>
                                <div className="vertical grid-block">
                                    <h3 className="dashboard-quicklink__title">{TS("Customers")}</h3>
                                    <p className="no-margin">{TS("Create and manage customer accounts")}.</p>
                                    <div className="dashboard-quicklink__links">
                                        <a href="data/customers">{TS("View")}</a>&#8226;
                                        {permissions.customer?.canCreate && (
                                            <>
                                                <a href="data/customers/new">{TS("Create")}</a>
                                                <span>&#8226;</span>
                                            </>
                                        )}
                                        {(permissions.customer?.canCreate || permissions.customer?.canEdit) && (
                                            <>
                                                <a href="import/customers">{TS("Import")}</a>
                                                <span>&#8226;</span>
                                            </>
                                        )}
                                        <a href="export/customers">{TS("Export")}</a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {permissions.userProfile?.canView && (
                        <div className="small-12 medium-6 large-4 grid-block">
                            <div className="grid-block dashboard-quicklink">
                                <i className="fa-light fa-earth-americas dashboard-quicklink__image dashboard-quicklink__image--users"></i>
                                <div className="vertical grid-block">
                                    <h3 className="dashboard-quicklink__title">{TS("Website Users")}</h3>
                                    <p className="no-margin">{TS("Create and manage website users")}.</p>
                                    <div className="dashboard-quicklink__links">
                                        <a href="data/userProfiles">{TS("View")}</a>&#8226;
                                        {permissions.userProfile?.canCreate && (
                                            <>
                                                <a href="data/userProfiles/new">{TS("Create")}</a>
                                                <span>&#8226;</span>
                                            </>
                                        )}
                                        {(permissions.userProfile?.canCreate || permissions.userProfile?.canEdit) && (
                                            <>
                                                <a href="import/userProfiles">{TS("Import")}</a>
                                                <span>&#8226;</span>
                                            </>
                                        )}
                                        <a href="export/userProfiles">{TS("Export")}</a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <div className="grid-content">
                    <h2>{TS("Upcoming Features")}</h2>
                </div>
                <div className="grid-block wrap">
                    <div className="small-12 medium-6 large-4 grid-block">
                        <div
                            className="grid-block dashboard-quicklink dashboard-quicklink__link"
                            onClick={() =>
                                openExternalLink("https://www.optimizely.com/product-updates/commerce/", "Roadmap")
                            }
                        >
                            <i className="fa-light fa-network-wired dashboard-quicklink__image dashboard-quicklink__image--roadmap"></i>
                            <div className="vertical grid-block">
                                <h3 className="dashboard-quicklink__title">{TS("Roadmap")}</h3>
                                <p className="no-margin">{TS("See the current roadmap of the Configured Commerce.")}</p>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="grid-content">
                    <h2>{TS("Help")}</h2>
                </div>
                <div className="grid-block wrap">
                    <div className="small-12 medium-6 large-4 grid-block">
                        <div
                            className="grid-block dashboard-quicklink dashboard-quicklink__link"
                            onClick={() =>
                                openExternalLink(
                                    "https://support.optimizely.com/hc/en-us/categories/4415978299277-Configured-Commerce",
                                    "Documentation",
                                )
                            }
                        >
                            <i className="fa-light fa-book-open dashboard-quicklink__image dashboard-quicklink__image--documentation"></i>
                            <div className="vertical grid-block">
                                <h3 className="dashboard-quicklink__title">{TS("Documentation")}</h3>
                                <p className="no-margin">{TS("Open docs for Configured Commerce.")}</p>
                            </div>
                        </div>
                    </div>
                    <div className="small-12 medium-6 large-4 grid-block">
                        <div
                            className="grid-block dashboard-quicklink dashboard-quicklink__link"
                            onClick={() =>
                                openExternalLink("https://support.optimizely.com/hc/en-us/requests/new", "Support")
                            }
                        >
                            <i className="fa-light fa-phone dashboard-quicklink__image dashboard-quicklink__image--support"></i>
                            <div className="vertical grid-block">
                                <h3 className="dashboard-quicklink__title">{TS("Support")}</h3>
                                <p className="no-margin">{TS("Open a new ticket with Optimizely Support.")}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div
                className={"modal--medium modal-overlay " + (contentEditorModalIsOpen ? "is-active" : "")}
                id="contentEditorModal"
            >
                <aside className={"modal " + (contentEditorModalIsOpen ? "is-active" : "")}>
                    <div className="grid-block vertical">
                        <div className="modal-header">
                            <h4 className="modal-title">{TS("Content Editor CMS")}</h4>
                            <a className="close-button right" onClick={() => setContentEditorModalIsOpen(false)}>
                                <i className="fa-light fa-xmark"></i>
                            </a>
                        </div>
                        <div className="grid-block">
                            <div className="grid-block vertical">
                                <h5>{TS("Select Website")}</h5>
                                <div className="grid-block">
                                    <ul className="menu-action-list websites-list">
                                        {websitesData?.value?.map(website => (
                                            <li>
                                                <a
                                                    className="menu-action-list__action"
                                                    onClick={() => openWebsiteContentAdmin(website)}
                                                    target="_blank"
                                                >
                                                    <i className="menu-action-list__icon fa-light fa-up-right-from-square"></i>{" "}
                                                    <span className="menu-action-list__label">{website.name}</span>
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <a className="button float-right" onClick={() => setContentEditorModalIsOpen(false)}>
                                {TS("Cancel")}
                            </a>
                        </div>
                    </div>
                </aside>
            </div>
            <div
                className={"modal--medium modal-overlay " + (importExportModalIsOpen ? "is-active" : "")}
                id="importExportModal"
            >
                <aside className={"modal " + (importExportModalIsOpen ? "is-active" : "")}>
                    <div className="grid-block vertical">
                        <div className="modal-header">
                            <h4 className="modal-title">{TS("Import / Export")}</h4>
                            <a className="close-button right" onClick={() => setImportExportModalIsOpen(false)}>
                                <i className="fa-light fa-xmark"></i>
                            </a>
                        </div>
                        <div className="modal grid-block vertical form-content">
                            <div className="grid-block  grid-content">
                                <span className="importExport__label">
                                    {TS("What would you like to Import or Export")}?
                                </span>
                                <div>
                                    {actionNameOptionFor("import", "Import")}
                                    {actionNameOptionFor("export", "Export")}
                                </div>
                                <hr />
                                <div className="grid-block vertical optionslist">
                                    <ul>
                                        {definitionNameOptionFor("product", "products", "Products")}
                                        {definitionNameOptionFor("customer", "customers", "Customers")}
                                        {definitionNameOptionFor("category", "categories", "Categories")}
                                        {definitionNameOptionFor("userProfile", "userProfiles", "Users")}
                                        {definitionNameOptionFor(
                                            "translationDictionary",
                                            "translationDictionaries",
                                            "Translation Dictionary",
                                        )}
                                        {definitionNameOptionFor(
                                            "translationProperty",
                                            "translationProperties",
                                            "Field Translations",
                                        )}
                                        {definitionNameOptionFor("htmlRedirect", "htmlRedirects", "URL Redirects")}
                                    </ul>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <input
                                    type="submit"
                                    className="button float-right"
                                    value={TS("Continue")}
                                    onClick={() => navigate(`${actionName}/${definitionName}`)}
                                />
                                <a
                                    className="button secondary float-right"
                                    onClick={() => setImportExportModalIsOpen(false)}
                                >
                                    {TS("Cancel")}
                                </a>
                            </div>
                        </div>
                    </div>
                </aside>
            </div>
        </>
    );
};

angular
    .module("insite-admin")
    .component("dashboard", react2Angular(Dashboard, ["permissions"], ["adminSessionService", "websiteService"]));
